import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, Link2, BarChart3, Wallet, Handshake, Check, Lock, ArrowRight } from "lucide-react";
import api from "../../lib/api";

// Feature flag: temporarily hide the SaaS monthly-subscription pricing while ChartzFx
// runs on the 50/50 profit-share model. Flip to `true` to restore the pricing grid.
const SHOW_SAAS_PRICING = false;

const Shell = ({ eyebrow, title, children }) => (
  <div className="max-w-5xl mx-auto px-6 py-20">
    <p className="label-xs mb-3">{eyebrow}</p>
    <h1 className="text-3xl sm:text-4xl tracking-tight font-medium text-white mb-10">{title}</h1>
    {children}
  </div>
);

export function About() {
  return (
    <Shell eyebrow="Company" title="Built for transparent money management">
      <div className="space-y-6 text-sm sm:text-base text-gray-400 leading-relaxed max-w-3xl">
        <p>ChartzFx exists because managed FX and CFD trading has a trust problem. Investors wire money into black boxes; managers drown in spreadsheets and screenshots.</p>
        <p>We built a third way: a <span className="text-white">non-custodial control room</span>. Clients keep their capital in their own MT5 broker accounts. Managers connect those accounts, copy trades from master strategies, and every stakeholder sees live equity, drawdown and performance — automatically.</p>
        <p>We are not a broker, exchange, prop firm or fund. We never touch client money, never promise returns, and never process deposits or withdrawals. We are the analytics, automation and reporting layer that professional managers deserve.</p>
        <div className="grid sm:grid-cols-3 gap-5 pt-4">
          {[["2023", "Founded"], ["40+", "Countries"], ["99.9%", "Sync uptime"]].map(([v, l]) => (
            <div key={l} className="card-dark p-6"><p className="tabular text-2xl text-[#00F0FF]">{v}</p><p className="label-xs mt-2">{l}</p></div>
          ))}
        </div>
      </div>
    </Shell>
  );
}

export function FeaturesPage() {
  const groups = [
    ["For Investors", ["Live balance, equity & daily P&L", "Equity curve and drawdown charts", "Open trades & full trade history", "PDF reports: daily, monthly, statements", "Broker-side withdrawals — we never hold funds"]],
    ["For Account Managers", ["Client onboarding with KYC & agreement tracking", "Master account & strategy management", "Subscriber risk allocation & lot multipliers", "Pause / resume / remove subscribers", "AUM, win-rate & drawdown analytics"]],
    ["For Platform Admins", ["User & role management", "Subscription plans", "Audit logs of every sensitive action", "Broker & country management", "Support tickets", "System-wide analytics"]],
  ];
  return (
    <Shell eyebrow="Product" title="One platform, three control rooms">
      <div className="grid md:grid-cols-3 gap-5">
        {groups.map(([title, items]) => (
          <div key={title} className="card-dark p-7">
            <h3 className="text-lg text-white mb-5">{title}</h3>
            <ul className="space-y-3">{items.map((i) => <li key={i} className="text-sm text-gray-400 flex gap-2"><span className="text-[#00F0FF]">▸</span>{i}</li>)}</ul>
          </div>
        ))}
      </div>
    </Shell>
  );
}

export function Pricing() {
  const [data, setData] = useState({ plans: [], support: { whatsapp: "+91 8107649663", whatsapp_link: "https://wa.me/918107649663", email: "support@chartzfx.com" } });
  useEffect(() => { api.get("/subscription/plans").then(({ data }) => setData(data)).catch(() => {}); }, []);
  const support = data.support;

  // ─── PROFIT-SHARE MODE (default, while SHOW_SAAS_PRICING is false) ─────────────
  if (!SHOW_SAAS_PRICING) {
    return (
      <div className="relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#7000FF]/15 blur-[180px] pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 py-20" data-testid="pricing-profit-share">
          <p className="label-xs mb-3 tracking-[0.3em]" data-testid="ps-eyebrow">Managed Copy-Trading</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-white leading-[1.05]" data-testid="ps-heading">
            Managed XAUUSD Copy Trading. <span className="bg-gradient-to-r from-[#7000FF] via-[#B074FF] to-[#00F0FF] bg-clip-text text-transparent">You Share Only the Profit.</span>
          </h1>
          <div className="mt-6 max-w-2xl text-gray-400 text-base leading-relaxed space-y-1" data-testid="ps-subhead">
            <p>Our manager trades Gold (XAUUSD) from a central account and mirrors approved trades to your connected MT5 account. You keep <span className="text-white">50% of eligible net profit</span>.</p>
            <p className="text-gray-500">No upfront platform fee — we only earn when you do. Your funds always stay in your own broker account.</p>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="ps-free-badge">
            <div className="inline-flex items-center gap-3 rounded-2xl border border-[#00E676]/30 bg-[#00E676]/[0.06] px-5 py-3">
              <span className="tabular text-3xl sm:text-4xl font-medium text-[#00E676]">50/50</span>
              <div>
                <p className="text-sm text-white font-medium">Eligible-Net-Profit Share</p>
                <p className="text-xs text-gray-500">You keep 50% — profit share applies to gains only, never losses.</p>
              </div>
            </div>
            <div className="inline-flex items-center gap-3 rounded-2xl border border-[#00F0FF]/30 bg-[#00F0FF]/[0.06] px-5 py-3">
              <span className="tabular text-2xl sm:text-3xl font-medium text-[#00F0FF]">$1,000</span>
              <div>
                <p className="text-sm text-white font-medium">Minimum Equity</p>
                <p className="text-xs text-gray-500">Required account equity to be eligible.</p>
              </div>
            </div>
          </div>

          {/* How it works — 5 steps */}
          <div className="mt-14">
            <p className="label-xs mb-6">How it works</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {[
                { n: "01", icon: UserPlus,  title: "Create Account", text: "Sign up for a ChartzFx account in under two minutes." },
                { n: "02", icon: Link2,     title: "Connect MT5",    text: "Securely link your MT5 account with at least $1,000 equity." },
                { n: "03", icon: Handshake, title: "Get Approved",   text: "We review your account and confirm eligibility before any trading." },
                { n: "04", icon: BarChart3, title: "Auto Copy",      text: "Approved XAUUSD trades mirror to your account with a protective stop-loss." },
                { n: "05", icon: Wallet,    title: "Friday Settlement", text: "Keep 50% of eligible net profit, reconciled weekly every Friday." },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.n} data-testid={`ps-step-${s.n}`}
                       className="relative rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-5 hover:border-[#7000FF]/50 hover:bg-white/[0.04] transition-all duration-300 group"
                       style={{ animationDelay: `${i * 90}ms` }}>
                    <div className="flex items-start justify-between mb-4">
                      <span className="tabular text-[10px] tracking-[0.3em] text-[#7000FF] font-medium">{s.n}</span>
                      <Icon size={16} className="text-[#00F0FF] group-hover:scale-110 transition-transform" />
                    </div>
                    <h3 className="text-white text-sm font-medium mb-2">{s.title}</h3>
                    <p className="text-xs text-gray-500 leading-relaxed">{s.text}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Why ChartzFx + Important Notice */}
          <div className="mt-14 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] backdrop-blur-xl p-8" data-testid="ps-why-card">
              <p className="label-xs mb-5">Why ChartzFx</p>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                {[
                "50/50 eligible-net-profit share — no fee on losses",
                "Manager-controlled XAUUSD execution",
                "Mandatory protective stop-loss on every trade",
                "You keep full ownership of your account",
                "Funds always stay in your own broker account",
                "Real-time account & performance monitoring",
                "Weekly Friday profit settlement",
                ].map((b, i) => (
                  <li key={b} data-testid={`ps-benefit-${i}`} className="flex items-start gap-3 text-sm text-gray-300">
                    <span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#00E676]/10 text-[#00E676] shrink-0">
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-[#7000FF]/30 bg-gradient-to-br from-[#7000FF]/[0.08] via-transparent to-[#00F0FF]/[0.04] backdrop-blur-xl p-8" data-testid="ps-notice-card">
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#7000FF]/20 text-[#B074FF]"><Lock size={15} /></span>
                <p className="label-xs">Important Notice</p>
              </div>
              <p className="text-white text-sm font-medium mb-3">ChartzFx never holds client funds.</p>
              <p className="text-sm text-gray-400 leading-relaxed">
                Your capital always remains in your own MT5 broker account. We only execute trades on authorized connected accounts.
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-14 flex flex-col sm:flex-row gap-4 sm:items-center" data-testid="ps-ctas">
            <Link to="/register" data-testid="ps-primary-cta"
                  className="group relative inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm font-medium text-white bg-gradient-to-r from-[#7000FF] to-[#B074FF] shadow-[0_10px_40px_-10px_rgba(112,0,255,0.6)] hover:shadow-[0_18px_50px_-8px_rgba(176,116,255,0.7)] transition-all duration-300 hover:-translate-y-0.5">
              Get Started
              <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <Link to="/login" data-testid="ps-secondary-cta"
               className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full text-sm text-gray-200 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:border-white/25 transition-all">
              Sign In
            </Link>
          </div>

          <p className="mt-10 text-[11px] text-gray-600 leading-relaxed max-w-3xl" data-testid="ps-risk-disclosure">
            Risk disclosure: Trading leveraged instruments such as XAUUSD (Gold) carries a significant risk of loss and is not suitable for every investor. ChartzFx does not guarantee profits, weekly returns, or protection of your capital. Past performance does not indicate future results. The 50/50 share applies to eligible net profit only. Only trade with capital you can afford to lose.
          </p>
        </div>
      </div>
    );
  }

  // ─── SaaS PRICING MODE (behind SHOW_SAAS_PRICING flag — kept for restoration) ──
  return (
    <Shell eyebrow="Pricing" title="Choose the plan that scales with your book">
      <p className="text-sm text-gray-400 mb-10 max-w-2xl">
        SaaS monthly subscription. Payments are collected manually by our support team — no gateway integration yet.
      </p>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {data.plans.map((p) => {
          const isEnterprise = p.code === "ENTERPRISE";
          const isSales = isEnterprise;
          const whatsappMsg = encodeURIComponent(`Hi ChartzFx, I'd like to ${isSales ? "discuss the Enterprise plan" : `subscribe to the ${p.name} plan (${p.price_display}/${p.period})`}.`);
          const waHref = `${support.whatsapp_link}?text=${whatsappMsg}`;
          const emailHref = `mailto:${support.email}?subject=${encodeURIComponent(isSales ? "Enterprise plan enquiry" : `Subscribe: ${p.name} plan`)}&body=${whatsappMsg}`;
          return (
            <div key={p.code} data-testid={`pricing-plan-${p.code.toLowerCase()}`}
                 className={`card-dark p-7 flex flex-col ${p.highlighted ? "border-[#7000FF] shadow-[0_0_35px_rgba(112,0,255,0.25)]" : ""}`}>
              {p.highlighted && <p className="text-[10px] tracking-[0.25em] text-[#00F0FF] mb-2">MOST POPULAR</p>}
              <p className="text-sm text-gray-400 uppercase tracking-wider">{p.name}</p>
              <div className="mt-2 min-h-[3.5rem]">
                {p.price_display === "Custom Pricing" ? (
                  <p className="tabular text-2xl text-white">Custom Pricing</p>
                ) : (
                  <p className="tabular text-3xl text-white">{p.price_display}
                    <span className="text-sm text-gray-500">/{p.period}</span></p>
                )}
              </div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="text-sm text-gray-400 flex gap-2"><span className="text-[#00E676]">✓</span>{f}</li>
                ))}
              </ul>
              <a href={waHref} target="_blank" rel="noopener noreferrer"
                 data-testid={`pricing-cta-${p.code.toLowerCase()}`}
                 className={`${p.highlighted ? "btn-primary" : "btn-outline"} block text-center mt-7 py-2.5 text-sm`}>
                {p.cta}
              </a>
              <a href={emailHref} className="text-[11px] text-center text-gray-500 mt-2 hover:text-[#00F0FF]">or email {support.email}</a>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-gray-600 mt-8">SaaS subscription fees only. ChartzFx never takes custody of client funds and never charges from client capital.</p>
    </Shell>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent. We'll reply within 24 hours.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail?.[0]?.msg || "Failed to send message");
    } finally { setBusy(false); }
  };
  return (
    <Shell eyebrow="Contact" title="Talk to the team">
      <div className="grid md:grid-cols-2 gap-10">
        <form onSubmit={submit} className="space-y-4">
          <input data-testid="contact-name-input" required minLength={2} className="input-dark" placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input data-testid="contact-email-input" required type="email" className="input-dark" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input data-testid="contact-subject-input" required minLength={2} className="input-dark" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
          <textarea data-testid="contact-message-input" required minLength={5} rows={5} className="input-dark resize-none" placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
          <button data-testid="contact-submit-button" disabled={busy} className="btn-primary px-7 py-2.5 text-sm disabled:opacity-50">{busy ? "Sending…" : "Send Message"}</button>
        </form>
        <div className="text-sm text-gray-400 space-y-4">
          <p><span className="text-white">Support:</span> support@chartzfx.com</p>
          <p><span className="text-white">Sales:</span> support@chartzfx.com</p>
          <p className="leading-relaxed">Enterprise or white-label enquiries? Mention your current AUM and number of accounts and we&apos;ll route you to the right specialist.</p>
        </div>
      </div>
    </Shell>
  );
}

export function Legal({ type }) {
  const privacy = type === "privacy";
  return (
    <Shell eyebrow="Legal" title={privacy ? "Privacy Policy" : "Terms & Conditions"}>
      <div className="space-y-6 text-sm text-gray-400 leading-relaxed max-w-3xl">
        {privacy ? (
          <>
            <p><span className="text-white">1. Data we collect.</span> Account details (name, email), MT5 connection metadata (broker, login, server), and encrypted credentials required to sync read-only trading data.</p>
            <p><span className="text-white">2. How we use it.</span> To provide performance analytics, trade copier management and reporting. We never sell personal data.</p>
            <p><span className="text-white">3. Security.</span> Credentials are AES-encrypted at rest, all traffic is TLS-encrypted, passwords are bcrypt-hashed, and sensitive actions are audit-logged.</p>
            <p><span className="text-white">4. Funds.</span> We never collect, hold or process client funds. All monetary operations occur at your broker.</p>
            <p><span className="text-white">5. Your rights.</span> You may request export or deletion of your data at any time via support@chartzfx.com.</p>
          </>
        ) : (
          <>
            <p><span className="text-white">1. Nature of service.</span> ChartzFx is a software platform for account connection, trade copier management, analytics and reporting. It is not a broker, dealer, exchange, custodian, or investment adviser.</p>
            <p><span className="text-white">2. No custody.</span> Client funds remain at all times in the client&apos;s own broker account. The platform cannot move, deposit or withdraw funds.</p>
            <p><span className="text-white">3. No guaranteed returns.</span> Trading leveraged products involves substantial risk of loss. Past performance shown on the platform does not guarantee future results.</p>
            <p><span className="text-white">4. Subscription fees.</span> ChartzFx is a monthly SaaS platform. Fees are collected manually via bank transfer / UPI / crypto arranged with support. There is no charge on client trading capital.</p>
            <p><span className="text-white">5. Acceptable use.</span> You agree not to misrepresent performance, resell access without a white-label agreement, or use the platform for unlawful activity.</p>
          </>
        )}
      </div>
    </Shell>
  );
}

export function RefundPolicy() {
  return (
    <Shell eyebrow="Legal" title="Refund Policy">
      <div className="space-y-6 text-sm text-gray-400 leading-relaxed max-w-3xl" data-testid="refund-policy-content">
        <p>Last updated: {new Date().toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}</p>

        <div>
          <p className="text-white font-medium mb-2">1. Nature of Fees</p>
          <p>ChartzFx is a monthly <span className="text-white">SaaS subscription</span> platform. Fees are charged for access to the platform (dashboards, analytics, AI signals, MT5 connection and reporting) — not on your trading capital or realised profits. Fees are collected manually by our support team; there is currently no online payment gateway.</p>
        </div>

        <div>
          <p className="text-white font-medium mb-2">2. Billing Cycle</p>
          <p>Subscriptions are billed monthly in advance. Access is activated by our admin team once payment is confirmed, and remains valid until the expiry date shown in your Subscription panel.</p>
        </div>

        <div>
          <p className="text-white font-medium mb-2">3. Refund Eligibility</p>
          <p>Refunds are considered on a case-by-case basis for the following situations:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><span className="text-white">Duplicate payment</span> — you accidentally paid for the same month twice. Full refund of the duplicate amount.</li>
            <li><span className="text-white">Immediate cancellation</span> — you request cancellation within 48 hours of a fresh activation and have not used the paid features. Pro-rata refund at our discretion.</li>
            <li><span className="text-white">Service outage</span> — platform-wide unavailability of more than 72 continuous hours during your billing month. Pro-rata credit or refund.</li>
            <li><span className="text-white">Wrong plan assigned</span> — admin activation error. Full correction of overpayment.</li>
          </ul>
        </div>

        <div>
          <p className="text-white font-medium mb-2">4. Non-Refundable Situations</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Change of mind mid-cycle after using the platform.</li>
            <li>Trading losses on your MT5 account — ChartzFx does not manage funds and does not guarantee returns.</li>
            <li>Downgrading to a lower plan mid-cycle — the higher plan remains active until expiry.</li>
            <li>Suspension due to policy violation.</li>
          </ul>
        </div>

        <div>
          <p className="text-white font-medium mb-2">5. How to Request a Refund</p>
          <p>Email <a href="mailto:support@chartzfx.com" className="text-[#00F0FF] hover:underline">support@chartzfx.com</a> within <span className="text-white">14 days</span> of the payment. Please include:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Your registered email</li>
            <li>Payment method and transaction reference</li>
            <li>Reason for refund (referencing an eligibility condition above)</li>
          </ul>
          <p className="mt-2">We respond to every request within 5 business days. Approved refunds are settled to the original payment method within 7 business days of approval.</p>
        </div>

        <div>
          <p className="text-white font-medium mb-2">6. Contact</p>
          <p>All refund-related communications should go to <a href="mailto:support@chartzfx.com" className="text-[#00F0FF] hover:underline">support@chartzfx.com</a> or WhatsApp <a href="https://wa.me/918107649663" className="text-[#00F0FF] hover:underline">+91 8107649663</a>. We do not process refunds over chat or social media — email is the record of truth.</p>
        </div>

        <p className="text-xs text-gray-600 pt-4 border-t border-white/5">
          ChartzFx reserves the right to update this policy from time to time. Material changes will be announced on the platform and via email at least 14 days before taking effect.
        </p>
      </div>
    </Shell>
  );
}
